import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
    Alert,
    Button,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";

import { parseISO } from "date-fns";

import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { AppointmentLocation } from "../../../../utils/types/session";
import {
    ClientEducation,
    ClientGender,
    ClientRace,
    ClientRelationship,
    ClientReligion,
    ClientSexualOrientation,
} from "../../../../utils/types/client";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";

import {
    TREATMENT_REVIEW_NOTE_LABELS,
    PSYCHOSOCIAL_ASSESSMENT_ALERT_TEXT,
} from "../../../../utils/constants";
import {
    emptyFamilyTreatmentReviewNote,
    IEditFamilyTreatmentReviewNotePayload,
    IGetClientNotes,
    ISessionNote,
    NoteTypes,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditFamilyTreatmentReviewProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function EditFamilyTreatmentReview({
    noteDetails,
}: EditFamilyTreatmentReviewProps) {
    const [localExpectedTerminationDate, setLocalExpectedTerminationDate] =
        React.useState<Date | null>(
            noteDetails?.note_content?.expected_termination_date
                ? parseISO(noteDetails?.note_content?.expected_termination_date)
                : null
        );

    const isSigning = React.useRef(false);

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );

    const {
        register,
        handleSubmit,
        getValues,
        control,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditFamilyTreatmentReviewNotePayload>({
        resolver: yupResolver(
            yup.object({
                family: yup.array().of(
                    yup.object().shape({
                        name: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .trim()
                                .required("Name is required"),
                            otherwise: yup.string(),
                        }),
                        gender: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup.string().required("Gender is required"),
                            otherwise: yup.string(),
                        }),
                        other_gender: yup
                            .string()
                            .when("gender", (gender: string, field) =>
                                gender === "other" && isSigning.current
                                    ? field.trim().required("Field is required")
                                    : field
                            ),
                        preferred_name: yup.string(),
                        pronouns: yup.string(),
                        religion: yup.string(),
                        education: yup.string(),
                        race: yup.string(),
                        ethnicity: yup.string(),
                        sexual_orientation: yup.string(),
                        other_sexual_orientation: yup
                            .string()
                            .when(
                                "sexual_orientation",
                                (sexual_orientation: string, field) =>
                                    sexual_orientation === "other" &&
                                    isSigning.current
                                        ? field
                                              .trim()
                                              .required("Field is required")
                                        : field
                            ),
                        current_employment: yup.string(),
                        current_employment_details: yup.string(),
                        relationship_status: yup.string(),
                        name_of_partner: yup.string(),
                        emergency_contact_information: yup.string(),
                        legal_problems: yup.string(),
                        anxiety_frequency: yup.string(),
                        anxiety_details: yup
                            .string()
                            .when("anxiety", (anxiety: boolean, field) =>
                                anxiety && isSigning.current
                                    ? field
                                          .trim()
                                          .required(
                                              "Anxiety details is required"
                                          )
                                          .min(
                                              20,
                                              "Anxiety details must contain at least 20 characters"
                                          )
                                    : field
                            ),
                        panic_attacks_freqency: yup.string(),
                        panic_attacks_details: yup
                            .string()
                            .when(
                                "panic_attacks",
                                (panic_attacks: boolean, field) =>
                                    panic_attacks && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Panic attacks details is required"
                                              )
                                              .min(
                                                  20,
                                                  "Panic attacks details must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        depression_freqency: yup.string(),
                        depression_details: yup
                            .string()
                            .when("depression", (depression: boolean, field) =>
                                depression && isSigning.current
                                    ? field
                                          .trim()
                                          .required(
                                              "Depression details is required"
                                          )
                                          .min(
                                              20,
                                              "Depression details must contain at least 20 characters"
                                          )
                                    : field
                            ),
                        selected_symptoms: yup.array().nullable(),
                        other_symptoms: yup.string(),
                        physical_symptoms: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .trim()
                                .required("Field is required")
                                .min(
                                    20,
                                    "Physical symptoms must contain at least 20 characters"
                                ),
                            otherwise: yup.string(),
                        }),
                        medication_physical_psychiatric: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .trim()
                                .required("Field is required")
                                .min(
                                    20,
                                    "Medication (physical/psychiatric) must contain at least 20 characters"
                                ),
                            otherwise: yup.string(),
                        }),
                        sleeping_habits_and_concerns: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .trim()
                                .required("Field is required")
                                .min(
                                    20,
                                    "Sleeping habits and concerns must contain at least 20 characters"
                                ),
                            otherwise: yup.string(),
                        }),
                        difficulties_with_appetite_or_eating_patterns: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .trim()
                                    .required("Field is required")
                                    .min(
                                        20,
                                        "Difficulties with appetite or eating patterns must contain at least 20 characters"
                                    ),
                                otherwise: yup.string(),
                            }),
                        been_so_distressed_you_seriously_wished_to_end_your_life:
                            yup.string().when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_been_so_distressed_you_seriously_wished_to_end_your_life:
                            yup
                                .string()
                                .when(
                                    "been_so_distressed_you_seriously_wished_to_end_your_life",
                                    (
                                        been_so_distressed_you_seriously_wished_to_end_your_life,
                                        field
                                    ) =>
                                        (been_so_distressed_you_seriously_wished_to_end_your_life ===
                                            "Today" ||
                                            been_so_distressed_you_seriously_wished_to_end_your_life ===
                                                "Recently") &&
                                        isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
                            yup.string().when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
                            yup
                                .string()
                                .when(
                                    "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live",
                                    (
                                        has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live,
                                        field
                                    ) =>
                                        has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        a_specific_plan_of_how_you_would_kill_yourself: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_a_specific_plan_of_how_you_would_kill_yourself:
                            yup
                                .string()
                                .when(
                                    "a_specific_plan_of_how_you_would_kill_yourself",
                                    (
                                        a_specific_plan_of_how_you_would_kill_yourself,
                                        field
                                    ) =>
                                        a_specific_plan_of_how_you_would_kill_yourself ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        access_to_weapons_or_means_of_hurting_self: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_access_to_weapons_or_means_of_hurting_self: yup
                            .string()
                            .when(
                                "access_to_weapons_or_means_of_hurting_self",
                                (
                                    access_to_weapons_or_means_of_hurting_self,
                                    field
                                ) =>
                                    access_to_weapons_or_means_of_hurting_self ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        made_serious_suicide_attempts: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .required("This field is required"),
                            otherwise: yup.string().nullable(),
                        }),
                        describe_made_serious_suicide_attempts: yup
                            .string()
                            .when(
                                "made_serious_suicide_attempts",
                                (made_serious_suicide_attempts, field) =>
                                    made_serious_suicide_attempts === "Yes" &&
                                    isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        purposely_done_something_to_hurt_yourself: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_done_something_to_hurt_yourself: yup
                            .string()
                            .when(
                                "purposely_done_something_to_hurt_yourself",
                                (
                                    purposely_done_something_to_hurt_yourself,
                                    field
                                ) =>
                                    purposely_done_something_to_hurt_yourself ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        heard_voices_telling_you_to_hurt_yourself: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_heard_voices_telling_you_to_hurt_yourself: yup
                            .string()
                            .when(
                                "heard_voices_telling_you_to_hurt_yourself",
                                (
                                    heard_voices_telling_you_to_hurt_yourself,
                                    field
                                ) =>
                                    heard_voices_telling_you_to_hurt_yourself ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        had_relatives_who_attempted_or_commited_sucide: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_had_relatives_who_attempted_or_commited_sucide:
                            yup
                                .string()
                                .when(
                                    "had_relatives_who_attempted_or_commited_sucide",
                                    (
                                        had_relatives_who_attempted_or_commited_sucide,
                                        field
                                    ) =>
                                        had_relatives_who_attempted_or_commited_sucide ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        had_thoughts_of_killing_or_seriously_hurting_someone:
                            yup.string().when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_had_thoughts_of_killing_or_seriously_hurting_someone:
                            yup
                                .string()
                                .when(
                                    "had_thoughts_of_killing_or_seriously_hurting_someone",
                                    (
                                        had_thoughts_of_killing_or_seriously_hurting_someone,
                                        field
                                    ) =>
                                        had_thoughts_of_killing_or_seriously_hurting_someone ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        heard_voices_telling_you_to_hurt_others: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_heard_voices_telling_you_to_hurt_others: yup
                            .string()
                            .when(
                                "heard_voices_telling_you_to_hurt_others",
                                (
                                    heard_voices_telling_you_to_hurt_others,
                                    field
                                ) =>
                                    heard_voices_telling_you_to_hurt_others ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        hurt_someone_or_destroyed_property_on_purpose: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_hurt_someone_or_destroyed_property_on_purpose:
                            yup
                                .string()
                                .when(
                                    "hurt_someone_or_destroyed_property_on_purpose",
                                    (
                                        hurt_someone_or_destroyed_property_on_purpose,
                                        field
                                    ) =>
                                        hurt_someone_or_destroyed_property_on_purpose ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        slapped_kicked_punched_someone_with_intent_to_harm: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_slapped_kicked_punched_someone_with_intent_to_harm:
                            yup
                                .string()
                                .when(
                                    "slapped_kicked_punched_someone_with_intent_to_harm",
                                    (
                                        slapped_kicked_punched_someone_with_intent_to_harm,
                                        field
                                    ) =>
                                        slapped_kicked_punched_someone_with_intent_to_harm ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        been_arrested_or_detained_for_violent_behavior: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_been_arrested_or_detained_for_violent_behavior:
                            yup
                                .string()
                                .when(
                                    "been_arrested_or_detained_for_violent_behavior",
                                    (
                                        been_arrested_or_detained_for_violent_behavior,
                                        field
                                    ) =>
                                        been_arrested_or_detained_for_violent_behavior ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        been_to_jail_for_any_reason: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .required("This field is required"),
                            otherwise: yup.string().nullable(),
                        }),
                        describe_been_to_jail_for_any_reason: yup
                            .string()
                            .when(
                                "been_to_jail_for_any_reason",
                                (been_to_jail_for_any_reason, field) =>
                                    been_to_jail_for_any_reason === "Yes" &&
                                    isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        been_on_probation_for_any_reason: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_been_on_probation_for_any_reason: yup
                            .string()
                            .when(
                                "been_on_probation_for_any_reason",
                                (been_on_probation_for_any_reason, field) =>
                                    been_on_probation_for_any_reason ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        do_you_have_access_to_guns: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .required("This field is required"),
                            otherwise: yup.string().nullable(),
                        }),
                        describe_do_you_have_access_to_guns: yup
                            .string()
                            .when(
                                "do_you_have_access_to_guns",
                                (do_you_have_access_to_guns, field) =>
                                    do_you_have_access_to_guns === "Yes" &&
                                    isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        patients_ability_and_capability_to_respond_to_treatment:
                            yup.array().nullable(),
                        details_for_diminished_poor_limited_quality_patient_oriented_evidence:
                            yup.string(),
                        anxiety: yup.boolean(),
                        panic_attacks: yup.boolean(),
                        depression: yup.boolean(),
                    })
                ),
                isClientPresent: yup.boolean(),
                appointment_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Appointment location is required"),
                    otherwise: yup.string(),
                }),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                }),
                secondary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                tertiary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_one: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_two: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                delivery_option: yup
                    .string()
                    .nullable()
                    .when(
                        "appointment_location",
                        (appointment_location, field) =>
                            appointment_location === "teletherapy" &&
                            isSigning.current
                                ? field.required(
                                      "Client teletherapy location is required"
                                  )
                                : field
                    ),
                provider_location: yup
                    .string()
                    .nullable()
                    .when(
                        "appointment_location",
                        (appointment_location, field) =>
                            appointment_location === "teletherapy" &&
                            isSigning.current
                                ? field.required(
                                      "Provider teletherapy location is required"
                                  )
                                : field
                    ),
                other_delivery_option: yup
                    .string()
                    .when("delivery_option", (delivery_option, field) =>
                        delivery_option === "Other" && isSigning.current
                            ? field
                                  .trim()
                                  .required(
                                      "Other client teletherapy location is required"
                                  )
                            : field
                    ),
                other_provider_location: yup
                    .string()
                    .when("provider_location", (provider_location, field) =>
                        provider_location === "Other" && isSigning.current
                            ? field
                                  .trim()
                                  .required(
                                      "Other provider teletherapy location is required"
                                  )
                            : field
                    ),
                consent_was_received_for_the_teletherapy_session: yup.boolean(),
                the_teletherapy_session_was_conducted_via_video: yup.boolean(),
                duration: yup.string(),
                what_are_the_problem_you_are_seeking_help_for: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string(),
                    }),
                people_present_at_session: yup.array().nullable(),
                other_person_present_at_session: yup.string(),
                type_of_treatment_recommended: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1, "At least one treatment type is required"),
                    otherwise: yup.array().nullable(),
                }),
                treatment_modality_intervention: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1, "At least one treatment modality is required"),
                    otherwise: yup.array().nullable(),
                }),
                other_treatment_modality_intervention: yup.string(),
                treatment_goal_one: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Treatment goal one is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_two: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Treatment goal two is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_three: yup.string(),
                medication_updates: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Medication updates is required")
                        .min(
                            20,
                            "Medication updates must contain at least 20 characters"
                        ),
                    otherwise: yup.string(),
                }),
                frequency_of_sessions: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                other_frequency_of_sessions: yup
                    .string()
                    .when(
                        "frequency_of_sessions",
                        (frequency_of_sessions, field) =>
                            frequency_of_sessions === "Other" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required(
                                          "Other frequency of sessions is required"
                                      )
                                : field
                    ),
                expected_termination_date: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .date()
                        .required("Expected termination date is required"),
                    otherwise: yup.date().nullable(),
                }),
                plan_of_care: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Plan of care is required"),
                    otherwise: yup.string(),
                }),
                subjective_report: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Subjective report is required")
                        .min(
                            20,
                            "Subjective report must contain at least 20 characters"
                        ),
                    otherwise: yup.string(),
                }),
                summary: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Summary is required")
                        .min(20, "Summary must contain at least 20 characters"),
                    otherwise: yup.string(),
                }),
                prognosis: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Prognosis is required"),
                    otherwise: yup.string(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
                  family:
                      noteDetails?.note_content?.family?.length > 0
                          ? noteDetails.note_content.family
                          : [
                                {
                                    ...emptyFamilyTreatmentReviewNote,
                                    name: `${noteDetails.client.first_name} ${noteDetails.client.last_name}`,
                                },
                            ],
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const isCheckboxChecked = (checkboxValue: string, key: string) => {
        return Boolean(
            noteDetails.note_content?.[key]?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "appointment_location",
            note?.note_content.appointment_location
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note?.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue("delivery_option", note.note_content.delivery_option);
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue("duration", note.note_content.duration);
        setValue("family", note.note_content.family);
        setValue(
            "family.0.name",
            `${noteDetails.client.first_name} ${noteDetails.client.last_name}` ||
                ""
        );
        setValue(
            "other_delivery_option",
            note.note_content.other_delivery_option
        );
        setValue(
            "other_person_present_at_session",
            note.note_content.other_person_present_at_session
        );
        setValue(
            "other_provider_location",
            note.note_content.other_provider_location
        );
        setValue(
            "other_treatment_modality_intervention",
            note.note_content.other_treatment_modality_intervention
        );
        setValue(
            "people_present_at_session",
            note.note_content.people_present_at_session
        );
        setValue(
            "expected_termination_date",
            note.note_content.date_of_next_session
        );
        setValue("plan_of_care", note.note_content.plan_of_care);
        setValue("prognosis", note.note_content.prognosis);
        setValue("provider_location", note.note_content.provider_location);
        setValue("subjective_report", note.note_content.subjective_report);
        setValue("summary", note.note_content.summary);
        setValue(
            "the_teletherapy_session_was_conducted_via_video",
            note.note_content.the_teletherapy_session_was_conducted_via_video
        );
        setValue("treatment_goal_one", note.note_content.treatment_goal_one);
        setValue(
            "treatment_goal_three",
            note.note_content.treatment_goal_three
        );
        setValue(
            "frequency_of_sessions",
            note.note_content.frequency_of_sessions
        );
        setValue(
            "other_frequency_of_sessions",
            note.note_content.other_frequency_of_sessions
        );
        setValue("treatment_goal_two", note.note_content.treatment_goal_two);
        setValue(
            "treatment_modality_intervention",
            note.note_content.treatment_modality_intervention
        );
        setValue(
            "type_of_treatment_recommended",
            note.note_content.type_of_treatment_recommended
        );
        setValue(
            "what_are_the_problem_you_are_seeking_help_for",
            note.note_content.what_are_the_problem_you_are_seeking_help_for
        );
        setShowPrompt(false);
    };

    const { fields, append, remove } = useFieldArray({
        control,
        name: "family",
    });

    // Get frequency for select
    const FrequencyForSelect = ["daily", "weekly", "monthly", "yearly"].map(
        (item) => ({
            label: makeStringFirstLetterCapital(item),
            value: item,
        })
    );

    const RelationshipForSelect = Object.values(ClientRelationship).map(
        (relationship) => ({
            label: removeEnumUnderscore(
                makeStringFirstLetterCapital(relationship)
            ),
            value: relationship,
        })
    );

    const GenderForSelect = Object.values(ClientGender).map((gender) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(gender)),
        value: gender,
    }));

    const ReligionForSelect = Object.values(ClientReligion).map((religion) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(religion)),
        value: religion,
    }));

    const EducationForSelect = Object.values(ClientEducation).map(
        (education) => ({
            label: removeEnumUnderscore(
                makeStringFirstLetterCapital(education)
            ),
            value: education,
        })
    );

    const SexualOrientationForSelect = Object.values(
        ClientSexualOrientation
    ).map((orientation) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(orientation)),
        value: orientation,
    }));

    const RaceForSelect = Object.values(ClientRace).map((race) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(race)),
        value: race,
    }));

    const AppointmentLocationsForSelect = Object.values(
        AppointmentLocation
    ).map((location) => {
        return {
            label: removeEnumUnderscore(makeStringFirstLetterCapital(location)),
            value: location,
        };
    });

    const clientName = `${noteDetails.client.first_name} ${noteDetails.client.last_name}`;

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}
            <div>
                <div className="mb-4 flex items-center">
                    <p className="note-label !mb-0 !text-base">
                        Family Count -<span>{fields.length}</span>
                    </p>
                    <div className="ml-[--spacing-items] flex items-center gap-x-[--spacing-items]">
                        <Button
                            onClick={() =>
                                append(emptyFamilyTreatmentReviewNote, {
                                    shouldFocus: false,
                                })
                            }
                        >
                            Add
                        </Button>
                        <Button
                            onClick={() => remove(fields.length - 1)}
                            disabled={fields.length === 1}
                            variant="secondary"
                        >
                            Remove
                        </Button>
                    </div>
                </div>
            </div>
            <h3 className="text-base font-medium mb-[--spacing-items]">
                Family Treatment Review Form
            </h3>
            {noteDetails.type === NoteTypes.FAMILY_PSYCHOSOCIAL_ASSESSMENT && (
                <div className="mb-2">
                    <Alert
                        description={PSYCHOSOCIAL_ASSESSMENT_ALERT_TEXT}
                        type="info"
                    />
                </div>
            )}
            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="family treatment review"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <FormCheckbox
                        {...register("isClientPresent")}
                        label={TREATMENT_REVIEW_NOTE_LABELS.isClientPresent}
                        defaultChecked={
                            noteDetails.note_content?.isClientPresent
                        }
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="appointment_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label={
                                    TREATMENT_REVIEW_NOTE_LABELS.appointment_location
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={AppointmentLocationsForSelect.find(
                                    (location) =>
                                        location.value ===
                                            watch("appointment_location") ||
                                        null
                                )}
                                options={AppointmentLocationsForSelect}
                                isRequired
                                hasError={!!errors.appointment_location}
                                errorText={errors.appointment_location?.message}
                            />
                        )}
                    />
                </div>
                {watch(`appointment_location`) ===
                    AppointmentLocation.TELETHERAPY && (
                    <>
                        <p className="note-label">
                            {TREATMENT_REVIEW_NOTE_LABELS.delivery_option}
                        </p>
                        <div className="fg fg-space-between three flex">
                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Home"
                                value="Home"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Home"
                                }
                            />
                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Office"
                                value="Office"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Office"
                                }
                            />
                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Other"
                                value="Other"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Other"
                                }
                            />
                        </div>
                        {errors.delivery_option ? (
                            <div className="note-error">
                                <p>Delivery option is required</p>
                            </div>
                        ) : null}
                        {watch(`delivery_option`) === "Other" ||
                        noteDetails.note_content?.delivery_option ===
                            "Other" ? (
                            <div className="fg">
                                <Input
                                    {...register(`other_delivery_option`)}
                                    label={
                                        TREATMENT_REVIEW_NOTE_LABELS.other_delivery_option
                                    }
                                    hasError={!!errors?.other_delivery_option}
                                    errorText={
                                        errors?.other_delivery_option?.message
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_delivery_option
                                    }
                                />
                            </div>
                        ) : null}
                    </>
                )}
                {watch(`appointment_location`) ===
                    AppointmentLocation.TELETHERAPY && (
                    <>
                        <p className="note-label">
                            {TREATMENT_REVIEW_NOTE_LABELS.provider_location}
                        </p>
                        <div className="fg fg-space-between three flex">
                            <RadioInput
                                {...register(`provider_location`)}
                                label="Home"
                                value="Home"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Home"
                                }
                            />
                            <RadioInput
                                {...register(`provider_location`)}
                                label="Office"
                                value="Office"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Office"
                                }
                            />
                            <RadioInput
                                {...register(`provider_location`)}
                                label="Other"
                                value="Other"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Other"
                                }
                            />
                        </div>
                        {errors.provider_location ? (
                            <div className="note-error">
                                <p>Provider location is required</p>
                            </div>
                        ) : null}
                        {watch(`provider_location`) === "Other" ||
                        noteDetails.note_content?.provider_location ===
                            "Other" ? (
                            <div className="fg">
                                <Input
                                    {...register(`other_provider_location`)}
                                    label={
                                        TREATMENT_REVIEW_NOTE_LABELS.other_provider_location
                                    }
                                    hasError={!!errors?.other_provider_location}
                                    errorText={
                                        errors?.other_provider_location?.message
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_provider_location
                                    }
                                />
                            </div>
                        ) : null}
                    </>
                )}
                {watch("appointment_location") ===
                AppointmentLocation.TELETHERAPY ? (
                    <div className="note-checkboxes">
                        <FormCheckbox
                            {...register(
                                "consent_was_received_for_the_teletherapy_session"
                            )}
                            label={
                                TREATMENT_REVIEW_NOTE_LABELS.consent_was_received_for_the_teletherapy_session
                            }
                            defaultChecked={
                                noteDetails.note_content
                                    ?.consent_was_received_for_the_teletherapy_session
                            }
                        />
                        <FormCheckbox
                            {...register(
                                "the_teletherapy_session_was_conducted_via_video"
                            )}
                            label={
                                TREATMENT_REVIEW_NOTE_LABELS.the_teletherapy_session_was_conducted_via_video
                            }
                            defaultChecked={
                                noteDetails.note_content
                                    ?.the_teletherapy_session_was_conducted_via_video
                            }
                        />
                    </div>
                ) : null}
                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TREATMENT_REVIEW_NOTE_LABELS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors?.diagnosis_codes?.type ===
                                    "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors?.diagnosis_codes?.message
                                }
                                isMulti
                                hideSelectedOptions
                                isRequired
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TREATMENT_REVIEW_NOTE_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TREATMENT_REVIEW_NOTE_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TREATMENT_REVIEW_NOTE_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TREATMENT_REVIEW_NOTE_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <h3 className="note-header">Identities</h3>
                {Array.from({ length: fields.length }, (_, i) => (
                    <React.Fragment key={`${i + 1} key`}>
                        <p className="note-header">
                            {i === 0
                                ? `Client - ${clientName}`
                                : `Collateral ${i}`}
                        </p>
                        <section id="step-one" className="mb-10">
                            {i !== 0 && (
                                <div className="fg">
                                    <Input
                                        {...register(`family.${i}.name`)}
                                        label="Name"
                                        defaultValue={
                                            watch(`family.${i}.name`) || ""
                                        }
                                        isRequired
                                        hasError={!!errors?.family?.[i]?.name}
                                        errorText={
                                            errors?.family?.[i]?.name?.message
                                        }
                                    />
                                </div>
                            )}
                            <div className="fg">
                                <Input
                                    {...register(`family.${i}.preferred_name`)}
                                    label="Preferred name"
                                    value={watch(`family.${i}.preferred_name`)}
                                    isOptional
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.gender`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Gender"
                                            placeholder=""
                                            value={
                                                GenderForSelect.find(
                                                    (gender) =>
                                                        gender.value ===
                                                            watch(
                                                                `family.${i}.gender`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) =>
                                                field.onChange(
                                                    (val as Option).value
                                                )
                                            }
                                            options={GenderForSelect}
                                            hasError={
                                                !!errors?.family?.[i]?.gender
                                            }
                                            errorText={
                                                errors?.family?.[i]?.gender
                                                    ?.message
                                            }
                                            isRequired
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.gender`) === "other" ? (
                                <div className="fg">
                                    <Input
                                        {...register(
                                            `family.${i}.other_gender`
                                        )}
                                        label="Specify other gender"
                                        value={watch(
                                            `family.${i}.other_gender`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]?.other_gender
                                        }
                                        errorText={
                                            errors?.family?.[i]?.other_gender
                                                ?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            ) : null}
                            <div className="fg">
                                <Input
                                    {...register(`family.${i}.pronouns`)}
                                    label="Pronouns"
                                    value={watch(`family.${i}.pronouns`)}
                                    isOptional
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.religion`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Religion"
                                            placeholder=""
                                            value={
                                                ReligionForSelect.find(
                                                    (religion) =>
                                                        religion.value ===
                                                            watch(
                                                                `family.${i}.religion`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={ReligionForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.education`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Education"
                                            placeholder=""
                                            value={
                                                EducationForSelect.find(
                                                    (education) =>
                                                        education.value ===
                                                        watch(
                                                            `family.${i}.education` ||
                                                                ""
                                                        )
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={EducationForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.race`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Race"
                                            placeholder=""
                                            value={
                                                RaceForSelect.find(
                                                    (race) =>
                                                        race.value ===
                                                            watch(
                                                                `family.${i}.race`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={RaceForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Input
                                    {...register(`family.${i}.ethnicity`)}
                                    label="Ethnicity"
                                    value={watch(`family.${i}.ethnicity`)}
                                    isOptional
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.sexual_orientation`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Sexual orientation"
                                            placeholder=""
                                            value={
                                                SexualOrientationForSelect.find(
                                                    (orientation) =>
                                                        orientation.value ===
                                                            watch(
                                                                `family.${i}.sexual_orientation`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={SexualOrientationForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.sexual_orientation`) ===
                            "other" ? (
                                <div className="fg">
                                    <Input
                                        {...register(
                                            `family.${i}.other_sexual_orientation`
                                        )}
                                        label="Specify other sexual orientation"
                                        value={watch(
                                            `family.${i}.other_sexual_orientation`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]
                                                ?.other_sexual_orientation
                                        }
                                        errorText={
                                            errors?.family?.[i]
                                                ?.other_sexual_orientation
                                                ?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            ) : null}
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.current_employment`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Current employment"
                                            value={watch(
                                                `family.${i}.current_employment`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <TextArea
                                    {...register(
                                        `family.${i}.current_employment_details`
                                    )}
                                    label="Current employment details"
                                    value={watch(
                                        `family.${i}.current_employment_details`
                                    )}
                                    isOptional
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.relationship_status`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Relationship status"
                                            placeholder=""
                                            value={
                                                RelationshipForSelect.find(
                                                    (relationship) =>
                                                        relationship.value ===
                                                            watch(
                                                                `family.${i}.relationship_status`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={RelationshipForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Input
                                    {...register(`family.${i}.name_of_partner`)}
                                    label="Name of partner"
                                    value={watch(`family.${i}.name_of_partner`)}
                                    isOptional
                                />
                            </div>
                            <div className="fg">
                                <TextArea
                                    {...register(
                                        `family.${i}.emergency_contact_information`
                                    )}
                                    label="Emergency contact information"
                                    value={watch(
                                        `family.${i}.emergency_contact_information`
                                    )}
                                    isOptional
                                />
                            </div>
                            <div className="fg">
                                <TextArea
                                    {...register(`family.${i}.legal_problems`)}
                                    label="Legal problems"
                                    value={watch(`family.${i}.legal_problems`)}
                                    isOptional
                                />
                            </div>
                        </section>
                    </React.Fragment>
                ))}
                <h3 className="note-header">Presenting Concerns</h3>
                <p className="note-label-req">
                    {
                        TREATMENT_REVIEW_NOTE_LABELS.what_are_the_problem_you_are_seeking_help_for
                    }
                </p>
                <div className="fg">
                    <TextArea
                        {...register(
                            "what_are_the_problem_you_are_seeking_help_for"
                        )}
                        defaultValue={
                            noteDetails.note_content
                                ?.what_are_the_problem_you_are_seeking_help_for
                        }
                        hasError={
                            !!errors.what_are_the_problem_you_are_seeking_help_for
                        }
                        errorText={
                            errors.what_are_the_problem_you_are_seeking_help_for
                                ?.message
                        }
                    />
                </div>
                <p className="note-label note-label-opt">Symptoms</p>
                {Array.from({ length: fields.length }, (_, i) => (
                    <React.Fragment key={`${i + 1} key`}>
                        <p className="note-header">
                            {i === 0
                                ? `Client - ${clientName}`
                                : `Collateral ${i}`}{" "}
                            {i !== 0 && watch(`family.${i}.name`)
                                ? `- ${
                                      watch(`family.${i}.name`) ||
                                      noteDetails?.note_content.family[i]?.name
                                  }`
                                : ""}
                        </p>
                        <section id="step-two" className="mb-10">
                            <div className="mb-[--spacing-form-items]">
                                <Controller
                                    name={`family.${i}.anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Anxiety"
                                            name="Anxiety"
                                            checked={watch(
                                                `family.${i}.anxiety`
                                            )}
                                            onChange={(e) => {
                                                field.onChange(
                                                    e.target.checked
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.anxiety`) && (
                                <div className="fg fg-space-between two flex">
                                    <Controller
                                        name={`family.${i}.anxiety_frequency`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                label="Frequency"
                                                placeholder=""
                                                onChange={(val) => {
                                                    if (val === null) {
                                                        field.onChange("");
                                                        return;
                                                    }
                                                    field.onChange(
                                                        (val as Option).value
                                                    );
                                                }}
                                                value={
                                                    FrequencyForSelect.find(
                                                        (frequency) =>
                                                            frequency.value ===
                                                                watch(
                                                                    `family.${i}.anxiety_frequency`
                                                                ) || ""
                                                    ) || null
                                                }
                                                options={FrequencyForSelect}
                                                isClearable
                                            />
                                        )}
                                    />
                                    <TextArea
                                        {...register(
                                            `family.${i}.anxiety_details`
                                        )}
                                        label={
                                            TREATMENT_REVIEW_NOTE_LABELS.anxiety_details
                                        }
                                        defaultValue={watch(
                                            `family.${i}.anxiety_details`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]
                                                ?.anxiety_details
                                        }
                                        errorText={
                                            errors?.family?.[i]?.anxiety_details
                                                ?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            )}
                            <div className="mb-[--spacing-form-items]">
                                <Controller
                                    name={`family.${i}.panic_attacks`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Panic attacks"
                                            checked={watch(
                                                `family.${i}.panic_attacks`
                                            )}
                                            onChange={(e) => {
                                                field.onChange(
                                                    e.target.checked
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.panic_attacks`) && (
                                <div className="fg fg-space-between two flex">
                                    <Controller
                                        name={`family.${i}.panic_attacks_freqency`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                label="Frequency"
                                                placeholder=""
                                                onChange={(val) => {
                                                    if (val === null) {
                                                        field.onChange("");
                                                        return;
                                                    }
                                                    field.onChange(
                                                        (val as Option).value
                                                    );
                                                }}
                                                value={
                                                    FrequencyForSelect.find(
                                                        (frequency) =>
                                                            frequency.value ===
                                                                watch(
                                                                    `family.${i}.panic_attacks_freqency`
                                                                ) || ""
                                                    ) || null
                                                }
                                                options={FrequencyForSelect}
                                                isClearable
                                            />
                                        )}
                                    />
                                    <TextArea
                                        {...register(
                                            `family.${i}.panic_attacks_details`
                                        )}
                                        label={
                                            TREATMENT_REVIEW_NOTE_LABELS.panic_attacks_details
                                        }
                                        defaultValue={watch(
                                            `family.${i}.panic_attacks_details`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]
                                                ?.panic_attacks_details
                                        }
                                        errorText={
                                            errors?.family?.[i]
                                                ?.panic_attacks_details?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            )}
                            <div className="mb-[--spacing-form-items]">
                                <Controller
                                    name={`family.${i}.depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Depression"
                                            checked={watch(
                                                `family.${i}.depression`
                                            )}
                                            onChange={(e) =>
                                                field.onChange(e.target.checked)
                                            }
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.depression`) && (
                                <div className="fg fg-space-between two flex">
                                    <Controller
                                        name={`family.${i}.depression_freqency`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.depression_freqency
                                                }
                                                placeholder=""
                                                onChange={(val) => {
                                                    if (val === null) {
                                                        field.onChange("");
                                                        return;
                                                    }
                                                    field.onChange(
                                                        (val as Option).value
                                                    );
                                                }}
                                                value={
                                                    FrequencyForSelect.find(
                                                        (frequency) =>
                                                            frequency.value ===
                                                                watch(
                                                                    `family.${i}.depression_freqency`
                                                                ) || ""
                                                    ) || null
                                                }
                                                options={FrequencyForSelect}
                                                isClearable
                                            />
                                        )}
                                    />
                                    <TextArea
                                        {...register(
                                            `family.${i}.depression_details`
                                        )}
                                        label={
                                            TREATMENT_REVIEW_NOTE_LABELS.depression_details
                                        }
                                        defaultValue={watch(
                                            `family.${i}.depression_details`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]
                                                ?.depression_details
                                        }
                                        errorText={
                                            errors?.family?.[i]
                                                ?.depression_details?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            )}
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Easily distracted"
                                            value="Easily distracted"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Easily distracted")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Easily distracted",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Easily distracted"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Impulsive"
                                            value="Impulsive"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Impulsive")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Impulsive",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Impulsive"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Paranoia"
                                            value="Paranoia"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Paranoia")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Paranoia",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Paranoia"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Alcohol"
                                            value="Alcohol"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Alcohol")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Alcohol",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Alcohol"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Anger outburst"
                                            value="Anger outburst"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Anger outburst")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Anger outburst",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Anger outburst"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Unable to feel pleasure"
                                            value="Unable to feel pleasure"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes(
                                                "Unable to feel pleasure"
                                            )}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Unable to feel pleasure",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Unable to feel pleasure"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Excessive energy"
                                            value="Excessive energy"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Excessive energy")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Excessive energy",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Excessive energy"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Recreational drug use"
                                            value="Recreational drug use"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes(
                                                "Recreational drug use"
                                            )}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Recreational drug use",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Recreational drug use"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Tobacco"
                                            value="Tobacco"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Tobacco")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Tobacco",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Tobacco"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Racing thoughts"
                                            value="Racing thoughts"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Racing thoughts")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Racing thoughts",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Racing thoughts"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.other_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label={
                                                TREATMENT_REVIEW_NOTE_LABELS.other_symptoms
                                            }
                                            value={watch(
                                                `family.${i}.other_symptoms`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.physical_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label={
                                                TREATMENT_REVIEW_NOTE_LABELS.physical_symptoms
                                            }
                                            value={watch(
                                                `family.${i}.physical_symptoms`
                                            )}
                                            onChange={field.onChange}
                                            isRequired
                                            errorText={
                                                errors.family?.[i]
                                                    ?.physical_symptoms?.message
                                            }
                                            hasError={
                                                !!errors.family?.[i]
                                                    ?.physical_symptoms
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.medication_physical_psychiatric`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label={
                                                TREATMENT_REVIEW_NOTE_LABELS.medication_physical_psychiatric
                                            }
                                            value={watch(
                                                `family.${i}.medication_physical_psychiatric`
                                            )}
                                            onChange={field.onChange}
                                            isRequired
                                            errorText={
                                                errors.family?.[i]
                                                    ?.medication_physical_psychiatric
                                                    ?.message
                                            }
                                            hasError={
                                                !!errors.family?.[i]
                                                    ?.medication_physical_psychiatric
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.sleeping_habits_and_concerns`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label={
                                                TREATMENT_REVIEW_NOTE_LABELS.sleeping_habits_and_concerns
                                            }
                                            value={watch(
                                                `family.${i}.sleeping_habits_and_concerns`
                                            )}
                                            onChange={field.onChange}
                                            isRequired
                                            errorText={
                                                errors.family?.[i]
                                                    ?.sleeping_habits_and_concerns
                                                    ?.message
                                            }
                                            hasError={
                                                !!errors.family?.[i]
                                                    ?.sleeping_habits_and_concerns
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.difficulties_with_appetite_or_eating_patterns`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label={
                                                TREATMENT_REVIEW_NOTE_LABELS.difficulties_with_appetite_or_eating_patterns
                                            }
                                            value={watch(
                                                `family.${i}.difficulties_with_appetite_or_eating_patterns`
                                            )}
                                            onChange={field.onChange}
                                            isRequired
                                            errorText={
                                                errors.family?.[i]
                                                    ?.difficulties_with_appetite_or_eating_patterns
                                                    ?.message
                                            }
                                            hasError={
                                                !!errors.family?.[i]
                                                    ?.difficulties_with_appetite_or_eating_patterns
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </section>
                    </React.Fragment>
                ))}

                {Array.from({ length: fields.length }, (_, i) => (
                    <React.Fragment key={`${i + 1} key`}>
                        <section id="step-five" className="mb-10">
                            <p className="note-header">
                                Suicide risk assessment
                            </p>
                            <p className="note-header">
                                {i === 0
                                    ? `Client - ${clientName}`
                                    : `Collateral ${i}`}{" "}
                                {i !== 0 && watch(`family.${i}.name`)
                                    ? `- ${
                                          watch(`family.${i}.name`) ||
                                          noteDetails?.note_content.family[i]
                                              ?.name
                                      }`
                                    : ""}
                            </p>
                            <p className="note-label-req">
                                1. Been so distressed you seriously wished to
                                end your life?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Today"
                                            value="Today"
                                            name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                                                ) === "Today"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Recently"
                                            value="Recently"
                                            name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                                                ) === "Recently"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.been_so_distressed_you_seriously_wished_to_end_your_life ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                            ) === "Today" ||
                            watch(
                                `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                            ) === "Recently" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_been_so_distressed_you_seriously_wished_to_end_your_life`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                                }
                                                value={watch(
                                                    `family.${i}.describe_been_so_distressed_you_seriously_wished_to_end_your_life`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                2. Has anything happened recently to make you
                                feel you don’t want to live?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                            checked={
                                                watch(
                                                    `family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                            checked={
                                                watch(
                                                    `family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-header">
                                Have you had/Do you have -
                            </p>
                            <p className="note-label-req">
                                3. A specific plan of how you would kill
                                yourself?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.a_specific_plan_of_how_you_would_kill_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.a_specific_plan_of_how_you_would_kill_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.a_specific_plan_of_how_you_would_kill_yourself`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.a_specific_plan_of_how_you_would_kill_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.a_specific_plan_of_how_you_would_kill_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.a_specific_plan_of_how_you_would_kill_yourself`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.a_specific_plan_of_how_you_would_kill_yourself ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.a_specific_plan_of_how_you_would_kill_yourself`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_a_specific_plan_of_how_you_would_kill_yourself`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_a_specific_plan_of_how_you_would_kill_yourself
                                                }
                                                value={watch(
                                                    `family.${i}.describe_a_specific_plan_of_how_you_would_kill_yourself`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_a_specific_plan_of_how_you_would_kill_yourself
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_a_specific_plan_of_how_you_would_kill_yourself
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                4. Access to weapons or means of hurting self?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.access_to_weapons_or_means_of_hurting_self`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.access_to_weapons_or_means_of_hurting_self`}
                                            checked={
                                                watch(
                                                    `family.${i}.access_to_weapons_or_means_of_hurting_self`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.access_to_weapons_or_means_of_hurting_self`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.access_to_weapons_or_means_of_hurting_self`}
                                            checked={
                                                watch(
                                                    `family.${i}.access_to_weapons_or_means_of_hurting_self`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.access_to_weapons_or_means_of_hurting_self ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.access_to_weapons_or_means_of_hurting_self`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_access_to_weapons_or_means_of_hurting_self`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_access_to_weapons_or_means_of_hurting_self
                                                }
                                                value={watch(
                                                    `family.${i}.describe_access_to_weapons_or_means_of_hurting_self`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_access_to_weapons_or_means_of_hurting_self
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_access_to_weapons_or_means_of_hurting_self
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                5. Made serious suicide attempts
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.made_serious_suicide_attempts`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.made_serious_suicide_attempts`}
                                            checked={
                                                watch(
                                                    `family.${i}.made_serious_suicide_attempts`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.made_serious_suicide_attempts`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.made_serious_suicide_attempts`}
                                            checked={
                                                watch(
                                                    `family.${i}.made_serious_suicide_attempts`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.made_serious_suicide_attempts ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.made_serious_suicide_attempts`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_made_serious_suicide_attempts`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_made_serious_suicide_attempts
                                                }
                                                value={watch(
                                                    `family.${i}.describe_made_serious_suicide_attempts`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_made_serious_suicide_attempts
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_made_serious_suicide_attempts
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                6. Purposely done something to hurt yourself?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.purposely_done_something_to_hurt_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.purposely_done_something_to_hurt_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.purposely_done_something_to_hurt_yourself`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.purposely_done_something_to_hurt_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.purposely_done_something_to_hurt_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.purposely_done_something_to_hurt_yourself`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.purposely_done_something_to_hurt_yourself ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.purposely_done_something_to_hurt_yourself`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_done_something_to_hurt_yourself`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_done_something_to_hurt_yourself`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_done_something_to_hurt_yourself
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_done_something_to_hurt_yourself
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                7. Heard voices telling you to hurt yourself?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.heard_voices_telling_you_to_hurt_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.heard_voices_telling_you_to_hurt_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.heard_voices_telling_you_to_hurt_yourself`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.heard_voices_telling_you_to_hurt_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.heard_voices_telling_you_to_hurt_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.heard_voices_telling_you_to_hurt_yourself`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.heard_voices_telling_you_to_hurt_yourself ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.heard_voices_telling_you_to_hurt_yourself`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_heard_voices_telling_you_to_hurt_yourself`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_heard_voices_telling_you_to_hurt_yourself
                                                }
                                                value={watch(
                                                    `family.${i}.describe_heard_voices_telling_you_to_hurt_yourself`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_heard_voices_telling_you_to_hurt_yourself
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_heard_voices_telling_you_to_hurt_yourself
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                8. Had relatives who attempted or committed
                                suicide?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.had_relatives_who_attempted_or_commited_sucide`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.had_relatives_who_attempted_or_commited_sucide`}
                                            checked={
                                                watch(
                                                    `family.${i}.had_relatives_who_attempted_or_commited_sucide`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.had_relatives_who_attempted_or_commited_sucide`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.had_relatives_who_attempted_or_commited_sucide`}
                                            checked={
                                                watch(
                                                    `family.${i}.had_relatives_who_attempted_or_commited_sucide`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.had_relatives_who_attempted_or_commited_sucide ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.had_relatives_who_attempted_or_commited_sucide`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_had_relatives_who_attempted_or_commited_sucide`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_had_relatives_who_attempted_or_commited_sucide
                                                }
                                                value={watch(
                                                    `family.${i}.describe_had_relatives_who_attempted_or_commited_sucide`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_had_relatives_who_attempted_or_commited_sucide
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_had_relatives_who_attempted_or_commited_sucide
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                9. Had thoughts of killing or seriously hurting
                                someone?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`}
                                            checked={
                                                watch(
                                                    `family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`}
                                            checked={
                                                watch(
                                                    `family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.had_thoughts_of_killing_or_seriously_hurting_someone ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_had_thoughts_of_killing_or_seriously_hurting_someone`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                                }
                                                value={watch(
                                                    `family.${i}.describe_had_thoughts_of_killing_or_seriously_hurting_someone`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                10. Heard voices telling you to hurt others?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.heard_voices_telling_you_to_hurt_others`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.heard_voices_telling_you_to_hurt_others`}
                                            checked={
                                                watch(
                                                    `family.${i}.heard_voices_telling_you_to_hurt_others`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.heard_voices_telling_you_to_hurt_others`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.heard_voices_telling_you_to_hurt_others`}
                                            checked={
                                                watch(
                                                    `family.${i}.heard_voices_telling_you_to_hurt_others`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.heard_voices_telling_you_to_hurt_others ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.heard_voices_telling_you_to_hurt_others`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_heard_voices_telling_you_to_hurt_others`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_heard_voices_telling_you_to_hurt_others
                                                }
                                                value={watch(
                                                    `family.${i}.describe_heard_voices_telling_you_to_hurt_others`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_heard_voices_telling_you_to_hurt_others
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_heard_voices_telling_you_to_hurt_others
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                11. Hurt someone or destroyed property on
                                purpose?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.hurt_someone_or_destroyed_property_on_purpose`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.hurt_someone_or_destroyed_property_on_purpose`}
                                            checked={
                                                watch(
                                                    `family.${i}.hurt_someone_or_destroyed_property_on_purpose`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.hurt_someone_or_destroyed_property_on_purpose`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.hurt_someone_or_destroyed_property_on_purpose`}
                                            checked={
                                                watch(
                                                    `family.${i}.hurt_someone_or_destroyed_property_on_purpose`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.hurt_someone_or_destroyed_property_on_purpose ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.hurt_someone_or_destroyed_property_on_purpose`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_hurt_someone_or_destroyed_property_on_purpose`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_hurt_someone_or_destroyed_property_on_purpose
                                                }
                                                value={watch(
                                                    `family.${i}.describe_hurt_someone_or_destroyed_property_on_purpose`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_hurt_someone_or_destroyed_property_on_purpose
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_hurt_someone_or_destroyed_property_on_purpose
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                12. Slapped, kicked, punched someone with intent
                                to harm?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`}
                                            checked={
                                                watch(
                                                    `family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`}
                                            checked={
                                                watch(
                                                    `family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.slapped_kicked_punched_someone_with_intent_to_harm ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_slapped_kicked_punched_someone_with_intent_to_harm`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                                }
                                                value={watch(
                                                    `family.${i}.describe_slapped_kicked_punched_someone_with_intent_to_harm`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                13. Been arrested or detained for violent
                                behavior?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.been_arrested_or_detained_for_violent_behavior`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.been_arrested_or_detained_for_violent_behavior`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_arrested_or_detained_for_violent_behavior`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_arrested_or_detained_for_violent_behavior`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.been_arrested_or_detained_for_violent_behavior`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_arrested_or_detained_for_violent_behavior`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.been_arrested_or_detained_for_violent_behavior ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.been_arrested_or_detained_for_violent_behavior`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_been_arrested_or_detained_for_violent_behavior`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_been_arrested_or_detained_for_violent_behavior
                                                }
                                                value={watch(
                                                    `family.${i}.describe_been_arrested_or_detained_for_violent_behavior`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_been_arrested_or_detained_for_violent_behavior
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_been_arrested_or_detained_for_violent_behavior
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                14. Been to jail for any reason?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.been_to_jail_for_any_reason`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.been_to_jail_for_any_reason`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_to_jail_for_any_reason`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_to_jail_for_any_reason`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.been_to_jail_for_any_reason`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_to_jail_for_any_reason`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]?.been_to_jail_for_any_reason ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.been_to_jail_for_any_reason`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_been_to_jail_for_any_reason`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_been_to_jail_for_any_reason
                                                }
                                                value={watch(
                                                    `family.${i}.describe_been_to_jail_for_any_reason`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_been_to_jail_for_any_reason
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_been_to_jail_for_any_reason
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                15. Been on probation for any reason?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.been_on_probation_for_any_reason`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.been_on_probation_for_any_reason`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_on_probation_for_any_reason`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_on_probation_for_any_reason`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.been_on_probation_for_any_reason`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_on_probation_for_any_reason`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.been_on_probation_for_any_reason ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.been_on_probation_for_any_reason`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_been_on_probation_for_any_reason`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label={
                                                    TREATMENT_REVIEW_NOTE_LABELS.describe_been_on_probation_for_any_reason
                                                }
                                                value={watch(
                                                    `family.${i}.describe_been_on_probation_for_any_reason`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_been_on_probation_for_any_reason
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_been_on_probation_for_any_reason
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                16. Do you have access to guns?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.do_you_have_access_to_guns`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.do_you_have_access_to_guns`}
                                            checked={
                                                watch(
                                                    `family.${i}.do_you_have_access_to_guns`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.do_you_have_access_to_guns`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.do_you_have_access_to_guns`}
                                            checked={
                                                watch(
                                                    `family.${i}.do_you_have_access_to_guns`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]?.do_you_have_access_to_guns ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(`family.${i}.do_you_have_access_to_guns`) ===
                            "Yes" ? (
                                <div className="fg">
                                    <TextArea
                                        {...register(
                                            `family.${i}.describe_do_you_have_access_to_guns`
                                        )}
                                        label="Describe the situation"
                                        defaultValue={
                                            noteDetails.note_content
                                                ?.describe_do_you_have_access_to_guns
                                        }
                                        hasError={
                                            !!errors.family?.[i]
                                                ?.describe_do_you_have_access_to_guns
                                        }
                                        errorText={
                                            errors.family?.[i]
                                                ?.describe_do_you_have_access_to_guns
                                                ?.message
                                        }
                                    />
                                </div>
                            ) : null}
                            <div>
                                <h3 className="note-label-req">
                                    Patient&apos;s ability and capability to
                                    respond to treatment
                                </h3>
                                <div className="note-checkboxes">
                                    <Controller
                                        name={`family.${i}.patients_ability_and_capability_to_respond_to_treatment`}
                                        control={control}
                                        render={({ field }) => (
                                            <FormCheckbox
                                                label="Consistent, good-quality patient-oriented evidence"
                                                value="Consistent, good-quality patient-oriented evidence"
                                                checked={(
                                                    watch(
                                                        `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                    ) || []
                                                )?.includes(
                                                    "Consistent, good-quality patient-oriented evidence"
                                                )}
                                                onChange={(e) => {
                                                    const updatedValue = e
                                                        .target.checked
                                                        ? [
                                                              ...(watch(
                                                                  `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                              ) || []),
                                                              "Consistent, good-quality patient-oriented evidence",
                                                          ]
                                                        : watch(
                                                              `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                          )?.filter(
                                                              (symptom) =>
                                                                  symptom !==
                                                                  "Consistent, good-quality patient-oriented evidence"
                                                          );
                                                    field.onChange(
                                                        updatedValue
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`family.${i}.patients_ability_and_capability_to_respond_to_treatment`}
                                        control={control}
                                        render={({ field }) => (
                                            <FormCheckbox
                                                label="Diminished/poor or limited-quality patient-oriented evidence"
                                                value="Diminished/poor or limited-quality patient-oriented evidence"
                                                checked={(
                                                    watch(
                                                        `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                    ) || []
                                                )?.includes(
                                                    "Diminished/poor or limited-quality patient-oriented evidence"
                                                )}
                                                onChange={(e) => {
                                                    const updatedValue = e
                                                        .target.checked
                                                        ? [
                                                              ...(watch(
                                                                  `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                              ) || []),
                                                              "Diminished/poor or limited-quality patient-oriented evidence",
                                                          ]
                                                        : watch(
                                                              `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                          )?.filter(
                                                              (symptom) =>
                                                                  symptom !==
                                                                  "Diminished/poor or limited-quality patient-oriented evidence"
                                                          );
                                                    field.onChange(
                                                        updatedValue
                                                    );
                                                }}
                                            />
                                        )}
                                    />

                                    {(
                                        watch(
                                            `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                        ) || []
                                    )?.includes(
                                        "Diminished/poor or limited-quality patient-oriented evidence"
                                    ) && (
                                        <>
                                            <span className="note-label !mb-0">
                                                Please provide details for
                                                diminished/poor quality
                                                patient-orientation
                                            </span>
                                            <TextArea
                                                {...register(
                                                    `family.${i}.details_for_diminished_poor_limited_quality_patient_oriented_evidence`
                                                )}
                                                defaultValue={watch(
                                                    `family.${i}.details_for_diminished_poor_limited_quality_patient_oriented_evidence`
                                                )}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                                        ?.message
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                                {errors.family?.[i]
                                    ?.patients_ability_and_capability_to_respond_to_treatment ? (
                                    <div className="note-error">
                                        <p>This field is required</p>
                                    </div>
                                ) : null}
                            </div>
                        </section>
                    </React.Fragment>
                ))}

                <section id="step-three" className="mb-10">
                    <h3 className="note-header">Summary</h3>
                    <p className="note-label note-label-opt">
                        {TREATMENT_REVIEW_NOTE_LABELS.people_present_at_session}
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Patient"
                            value="Patient"
                            defaultChecked={isCheckboxChecked(
                                "Patient",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Stepfather"
                            value="Stepfather"
                            defaultChecked={isCheckboxChecked(
                                "Stepfather",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Spouse"
                            value="Spouse"
                            defaultChecked={isCheckboxChecked(
                                "Spouse",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Stepmother"
                            value="Stepmother"
                            defaultChecked={isCheckboxChecked(
                                "Stepmother",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Partner"
                            value="Partner"
                            defaultChecked={isCheckboxChecked(
                                "Partner",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Guardian"
                            value="Guardian"
                            defaultChecked={isCheckboxChecked(
                                "Guardian",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("other_person_present_at_session")}
                            label={
                                TREATMENT_REVIEW_NOTE_LABELS.other_person_present_at_session
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_person_present_at_session
                            }
                            isOptional
                        />
                    </div>
                    <p className="note-label-req">
                        {
                            TREATMENT_REVIEW_NOTE_LABELS.type_of_treatment_recommended
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("type_of_treatment_recommended")}
                            label="Individual"
                            value="Individual"
                            defaultChecked={isCheckboxChecked(
                                "Individual",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register("type_of_treatment_recommended")}
                            label="Family"
                            value="Family"
                            defaultChecked={isCheckboxChecked(
                                "Family",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register("type_of_treatment_recommended")}
                            label="Group"
                            value="Group"
                            defaultChecked={isCheckboxChecked(
                                "Group",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register("type_of_treatment_recommended")}
                            label="Collateral/Parenting"
                            value="Collateral/Parenting"
                            defaultChecked={isCheckboxChecked(
                                "Collateral/Parenting",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                    </div>
                    {errors.type_of_treatment_recommended ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {
                            TREATMENT_REVIEW_NOTE_LABELS.treatment_modality_intervention
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Cognitive/Behavioral"
                            value="Cognitive/Behavioral"
                            defaultChecked={isCheckboxChecked(
                                "Cognitive/Behavioral",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Behavioral Modification"
                            value="Behavioral Modification"
                            defaultChecked={isCheckboxChecked(
                                "Behavioral Modification",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Supportive"
                            value="Supportive"
                            defaultChecked={isCheckboxChecked(
                                "Supportive",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Marital/Couples Therapy"
                            value="Marital/Couples Therapy"
                            defaultChecked={isCheckboxChecked(
                                "Marital/Couples Therapy",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Family Therapy"
                            value="Family Therapy"
                            defaultChecked={isCheckboxChecked(
                                "Family Therapy",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Stress Management"
                            value="Stress Management"
                            defaultChecked={isCheckboxChecked(
                                "Stress Management",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Psychodynamic"
                            value="Psychodynamic"
                            defaultChecked={isCheckboxChecked(
                                "Psychodynamic",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Parent Training"
                            value="Parent Training"
                            defaultChecked={isCheckboxChecked(
                                "Parent Training",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Crisis Intervention"
                            value="Crisis Intervention"
                            defaultChecked={isCheckboxChecked(
                                "Crisis Intervention",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                    </div>
                    {errors.treatment_modality_intervention ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    <div className="fg">
                        <Input
                            {...register(
                                "other_treatment_modality_intervention"
                            )}
                            label={
                                TREATMENT_REVIEW_NOTE_LABELS.other_treatment_modality_intervention
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_treatment_modality_intervention
                            }
                            isOptional
                        />
                    </div>
                    <p className="note-label">Treatment goals</p>
                    <div className="flex flex-col gap-y-[--spacing-form-items] mb-[--spacing-form-items]">
                        <Input
                            {...register("treatment_goal_one")}
                            label={
                                TREATMENT_REVIEW_NOTE_LABELS.treatment_goal_one
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_one
                            }
                            isRequired
                            hasError={!!errors.treatment_goal_one}
                            errorText={errors.treatment_goal_one?.message}
                        />
                        <Input
                            {...register("treatment_goal_two")}
                            label={
                                TREATMENT_REVIEW_NOTE_LABELS.treatment_goal_two
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_two
                            }
                            isRequired
                            hasError={!!errors.treatment_goal_two}
                            errorText={errors.treatment_goal_two?.message}
                        />
                        <Input
                            {...register("treatment_goal_three")}
                            label={
                                TREATMENT_REVIEW_NOTE_LABELS.treatment_goal_three
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_three
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("medication_updates")}
                            label={
                                TREATMENT_REVIEW_NOTE_LABELS.medication_updates
                            }
                            defaultValue={
                                noteDetails.note_content?.medication_updates
                            }
                            isRequired
                            hasError={!!errors.medication_updates}
                            errorText={errors.medication_updates?.message}
                        />
                    </div>
                    <h3 className="note-label-req">
                        {TREATMENT_REVIEW_NOTE_LABELS.frequency_of_sessions}
                    </h3>
                    <div className="note-radios">
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Weekly"
                            value="Weekly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Weekly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Bi-Weekly"
                            value="Bi-Weekly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Bi-Weekly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Monthly"
                            value="Monthly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Monthly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Other"
                            value="Other"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Other"
                            }
                        />
                    </div>

                    {errors.frequency_of_sessions ? (
                        <div className="note-error">
                            <p>Frequency of sessions is required</p>
                        </div>
                    ) : null}
                    {watch("frequency_of_sessions") === "Other" && (
                        <div className="fg">
                            <Input
                                {...register("other_frequency_of_sessions")}
                                label={
                                    TREATMENT_REVIEW_NOTE_LABELS.other_frequency_of_sessions
                                }
                                hasError={!!errors.other_frequency_of_sessions}
                                errorText={
                                    errors.other_frequency_of_sessions?.message
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.other_frequency_of_sessions
                                }
                            />
                        </div>
                    )}
                    <div className="fg">
                        <Controller
                            name="expected_termination_date"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={
                                        TREATMENT_REVIEW_NOTE_LABELS.expected_termination_date
                                    }
                                    onChange={(date) => {
                                        field.onChange(date);
                                        setLocalExpectedTerminationDate(date);
                                    }}
                                    selected={localExpectedTerminationDate}
                                    value={
                                        field.value
                                            ? formatDate(field.value, true)
                                            : undefined
                                    }
                                    hasError={
                                        !!errors.expected_termination_date
                                    }
                                    errorText={
                                        errors.expected_termination_date
                                            ?.type === "typeError"
                                            ? "Invalid date value"
                                            : errors.expected_termination_date
                                                  ?.message
                                    }
                                    shouldCloseOnSelect
                                    isRequired
                                />
                            )}
                        />
                    </div>
                    <p className="note-label-req">
                        {TREATMENT_REVIEW_NOTE_LABELS.plan_of_care}
                        <span>
                            (Will patient continue treatment? How many visits
                            and for how long if known?)
                        </span>
                    </p>
                    <div className="fg">
                        <Input
                            label=""
                            {...register("plan_of_care")}
                            hasError={!!errors.plan_of_care}
                            errorText={errors.plan_of_care?.message}
                            defaultValue={
                                noteDetails.note_content?.plan_of_care
                            }
                        />
                    </div>
                    <p className="note-label-req">
                        {TREATMENT_REVIEW_NOTE_LABELS.prognosis}
                    </p>
                    <div className="fg">
                        <Input
                            label=""
                            {...register("prognosis")}
                            defaultValue={noteDetails.note_content?.prognosis}
                            hasError={!!errors.prognosis}
                            errorText={errors.prognosis?.message}
                        />
                    </div>

                    <p className="note-label-req">
                        {TREATMENT_REVIEW_NOTE_LABELS.subjective_report}
                    </p>
                    <p className="note-label">
                        Please make sure to only include what the client has
                        reported. This should only tie back to what the client
                        reported in the session, re; symptoms, mood/affect,
                        ideations, interpersonal conflicts, etc. Do not include
                        your assessment or interpretation of the clients
                        symptoms here.
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("subjective_report")}
                            defaultValue={
                                noteDetails.note_content?.subjective_report
                            }
                            hasError={!!errors.subjective_report}
                            errorText={errors.subjective_report?.message}
                        />
                    </div>
                    <p className="note-label-req">
                        {" "}
                        {TREATMENT_REVIEW_NOTE_LABELS.summary}
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("summary")}
                            defaultValue={noteDetails.note_content?.summary}
                            hasError={!!errors.summary}
                            errorText={errors.summary?.message}
                        />
                    </div>
                </section>

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                            handleSubmit(handleSignNote)();
                        }}
                    />
                </div>
            </form>
        </>
    );
}
